import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { translateDebug, translateAbstractFlow } from "@/api/pretranslate";
import { createDraft } from "@/api/drafts";
import type { LangCode } from "@/api/types";
import { useToast } from "@/components/toast";
import BackendSelect from "./components/BackendSelect";

const MAX_CHARS = 5_000;

// Выбор движка временно скрыт: DeepL отдаёт HTTP 451 (регион), а дефолтный
// бэкенд (yandex) и так подставляется сервером, когда backend не передан.
// Поставить true — вернёт селектор. Код BackendSelect сохранён.
const BACKEND_SELECT_ENABLED: boolean = false;
const PREFERRED_BACKEND_KEY = "medtranslate.preferredBackend";
type ActiveMode = "file" | "text";
type LandingState = "empty" | "processing";

export default function TranslateLanding() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mode, setMode] = useState<ActiveMode>("text");
  const [sourceLang, setSourceLang] = useState<LangCode>("en");
  const [targetLang, setTargetLang] = useState<LangCode>("ru");
  const [text, setText] = useState("");
  const [state, setState] = useState<LandingState>("empty");
  const [backend, setBackend] = useState<string>(
    () => localStorage.getItem(PREFERRED_BACKEND_KEY) ?? "",
  );

  const owner =
    (auth.user?.profile?.preferred_username as string | undefined) ??
    (auth.user?.profile?.email as string | undefined) ??
    "anonymous";

  const translateMutation = useMutation({
    mutationFn: async (sourceText: string) => {
      setState("processing");
      const translateResult = await translateDebug({
        source_lang: sourceLang,
        target_lang: targetLang,
        text: sourceText,
        backend: backend || undefined,
      });
      if (!translateResult.ok) throw new Error(translateResult.error);
      const debug = translateResult.data;
      // Если сам бэкенд отказал (например, Yandex 403 / DeepL quota) — translate
      // вернёт HTTP 200 с translation.status === "error". Не создаём пустой
      // черновик, прокидываем сообщение в onError → toast.
      if (debug.translation?.status === "error") {
        const meta = (debug.translation.metadata ?? {}) as Record<
          string,
          unknown
        >;
        const detail =
          typeof meta.message === "string" ? meta.message : "ошибка бэкенда";
        const trimmed = detail.length > 400 ? detail.slice(0, 400) + "…" : detail;
        const backendName = debug.translation.backend_name ?? backend ?? "?";
        throw new Error(`Бэкенд «${backendName}»: ${trimmed}`);
      }
      const flatSnapshot = {
        terminology: debug.pretranslation?.terminology ?? {},
        risk: debug.risk ?? {
          status: "none",
          high_risk_terms: [],
          attention_terms: [],
          high_risk_fragments: [],
        },
        enforcement: debug.enforcement ?? {},
        quality: debug.quality ?? {},
        decision_summary: debug.decision_summary ?? null,
        domain: debug.pretranslation?.domain ?? null,
        translation: debug.translation,
        _debug: debug,
      };
      const draftResult = await createDraft({
        owner,
        source_lang: sourceLang,
        target_lang: targetLang,
        source_text: sourceText,
        target_text:
          debug.translation.final_text ??
          debug.translation.translated_text ??
          "",
        status: "open",
        metadata: {
          selectedBackend: backend || null,
        },
        pretranslate_snapshot: flatSnapshot as unknown as Record<
          string,
          unknown
        >,
      });
      if (!draftResult.ok) throw new Error(draftResult.error);
      return draftResult.data.draft;
    },
    onSuccess: (draft) => {
      // Сразу уходим в редактор по /translate/:draftId — пользователь
      // сразу видит результат с подсветкой и может править/копировать.
      navigate(`/translate/${draft.draft_id}`, { replace: true });
    },
    onError: (err: Error) => {
      toast(`Перевод не выполнен: ${err.message}`, "error");
      setState("empty");
    },
  });

  // Быстрый путь «Перевести абстракт»: показывает перевод инлайн (без draft'а
  // и без ухода в редактор), минимум времени от нажатия до результата.
  const [abstractResult, setAbstractResult] = useState<string | null>(null);
  const abstractMutation = useMutation({
    mutationFn: async (sourceText: string) => {
      setAbstractResult(null);
      return await translateAbstractFlow({
        source_lang: sourceLang,
        target_lang: targetLang,
        text: sourceText,
        backend: backend || undefined,
      });
    },
    onSuccess: (translated) => setAbstractResult(translated),
    onError: (err: Error) =>
      toast(`Перевод не выполнен: ${err.message}`, "error"),
  });

  const characters = text.length;
  const busy = translateMutation.isPending || abstractMutation.isPending;
  const canSubmit = characters > 0 && characters <= MAX_CHARS && !busy;

  if (state === "processing") {
    return <ProcessingPanel />;
  }

  return (
    <div className="cl-min__centered">
      <div className="cl-min__hero">
        <h1 className="cl-min__h1">Перевод медицинских текстов</h1>
        <p className="cl-min__sub">
          Вставьте текст или загрузите PDF / DOCX. Терминология применяется автоматически.
        </p>
        <p className="cl-min__sub" style={{ marginTop: 4 }}>
          <Link to="/translate/mine" style={{ color: "var(--blue-700)" }}>
            Мои переводы →
          </Link>
        </p>
      </div>

      <div className="cl-modetabs">
        <button
          className={"cl-modetabs__tab" + (mode === "file" ? " is-active" : "")}
          onClick={() => {
            setMode("file");
            toast("Загрузка файлов скоро будет доступна", "info");
          }}
          type="button"
        >
          Загрузить файл
        </button>
        <button
          className={"cl-modetabs__tab" + (mode === "text" ? " is-active" : "")}
          onClick={() => setMode("text")}
          type="button"
        >
          Вставить текст
        </button>
      </div>

      <div className="cl-controls">
        <LangPair
          from={sourceLang}
          to={targetLang}
          onSwap={() => {
            setSourceLang(targetLang);
            setTargetLang(sourceLang);
          }}
        />
        {BACKEND_SELECT_ENABLED && (
          <BackendSelect
            value={backend}
            onChange={(name) => {
              setBackend(name);
              if (name) localStorage.setItem(PREFERRED_BACKEND_KEY, name);
            }}
            disabled={translateMutation.isPending}
          />
        )}
      </div>

      {mode === "text" ? (
        <div className="cl-textinput">
          <textarea
            className="cl-textinput__area"
            placeholder="Введите или вставьте текст…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={MAX_CHARS}
          />
          <div className="cl-textinput__foot">
            <span className="cl-textinput__count">
              {characters.toLocaleString("ru-RU")} / {MAX_CHARS.toLocaleString("ru-RU")} символов
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="mt-btn mt-btn--lg"
                disabled={!canSubmit}
                onClick={() => abstractMutation.mutate(text)}
                type="button"
                title="Быстрый перевод: результат появится здесь же"
              >
                {abstractMutation.isPending ? "Переводим…" : "Перевести абстракт"}
              </button>
              <button
                className="mt-btn mt-btn--primary mt-btn--lg"
                disabled={!canSubmit}
                onClick={() => translateMutation.mutate(text)}
                type="button"
              >
                {translateMutation.isPending ? "Переводим…" : "Перевести"}
              </button>
            </div>
          </div>
          {abstractResult != null && (
            <div className="cl-card" style={{ marginTop: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <strong>Перевод</strong>
                <button
                  className="mt-btn mt-btn--sm mt-btn--ghost"
                  onClick={() => {
                    void navigator.clipboard?.writeText(abstractResult);
                    toast("Скопировано", "info");
                  }}
                  type="button"
                >
                  Копировать
                </button>
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{abstractResult}</div>
            </div>
          )}
        </div>
      ) : (
        <div className="cl-dropzone">
          <div className="cl-dropzone__title">Перетащите документ сюда</div>
          <div className="cl-dropzone__sub">
            <em>(загрузка файлов в разработке)</em>
          </div>
        </div>
      )}
    </div>
  );
}

function LangPair({
  from,
  to,
  onSwap,
}: {
  from: LangCode;
  to: LangCode;
  onSwap: () => void;
}) {
  return (
    <div className="cl-langpair">
      <button className="cl-langpair__btn" type="button">
        <span className="cl-langpair__lbl">Откуда</span>
        <span className="cl-langpair__lang">{labelFor(from)}</span>
      </button>
      <button
        className="cl-langpair__swap"
        title="Поменять языки местами"
        onClick={onSwap}
        type="button"
      >
        ⇄
      </button>
      <button className="cl-langpair__btn" type="button">
        <span className="cl-langpair__lbl">Куда</span>
        <span className="cl-langpair__lang">{labelFor(to)}</span>
      </button>
    </div>
  );
}

function labelFor(code: LangCode): string {
  switch (code) {
    case "en":
      return "Английский";
    case "ru":
      return "Русский";
    default:
      return code.toUpperCase();
  }
}

function ProcessingPanel() {
  return (
    <div className="cl-min__centered">
      <div className="cl-min__hero">
        <h1 className="cl-min__h1">Переводим…</h1>
        <p className="cl-min__sub">
          Это обычно занимает 20–40 секунд для документов до 1 000 слов.
        </p>
      </div>
      <div className="cl-card cl-card--working">
        <div className="cl-stages">
          <div className="cl-stage cl-stage--done">
            <span className="cl-stage__dot"></span>
            <span>Предобработка: терминология подобрана</span>
          </div>
          <div className="cl-stage cl-stage--active">
            <span className="cl-stage__dot"></span>
            <span>Перевод и проверки качества…</span>
          </div>
        </div>
      </div>
    </div>
  );
}

